import { MetadataSectionContainer } from "./MetadataSectionContainer"
import { FragmentType, graphql, useFragment } from "@/gql"
import { Text, Image } from "@/elements"
import { ChevronRight } from "react-feather"
import Link from "next/link"
import { ampli } from "@/ampli"
import { decodeGraphqlNodeId } from "@/tracking"

const MoreProjectsMetadataSectionFragment = graphql(`
  fragment MoreProjectsMetadataSection on IndividualCreative {
    id
    __typename
    handle
    name
    creativeWorkPortfolioProjects(first: 3, filters: { status: { exact: PUBLISHED } }, order: { randomize: true }) {
      edges {
        node {
          id
          title
          creatorRolesString
          thumbnail645w: thumbnailUrl(width: 645)
        }
      }
    }
  }
`)

interface Props {
  creative: FragmentType<typeof MoreProjectsMetadataSectionFragment> | null | undefined
}

export const MoreProjectsMetadataSection: React.FC<Props> = ({ ...props }) => {
  const creative = useFragment(MoreProjectsMetadataSectionFragment, props.creative)

  const workEdges = creative?.creativeWorkPortfolioProjects?.edges

  const title = `More from ${creative?.name}`

  if (!workEdges || workEdges.length === 0) {
    return null
  }

  return (
    <MetadataSectionContainer title={title}>
      <div className="flex flex-col gap-2">
        {workEdges?.map((project, index) => {
          const node = project?.node
          return (
            <Link
              key={project?.node?.id || index}
              onClick={() => {
                ampli.projectView({
                  project_id: decodeGraphqlNodeId(node?.id).numericalId || "",
                  entry_point: "more-projects-from-creative",
                })
              }}
              href={`/${creative?.handle}/projects/${node?.id}`}
              className="group flex w-full flex-row items-center">
              <div className="flex w-full min-w-0 flex-row items-center">
                <div className="relative size-[80px] flex-shrink-0 overflow-hidden rounded-md bg-bg-1">
                  <Image
                    alt={node?.title ?? ""}
                    className="h-full w-full object-cover transition-transform duration-300 ease-in-out group-hover:scale-[1.05]"
                    srcSetUrls={{
                      sm: node?.thumbnail645w ?? "",
                      md: node?.thumbnail645w ?? "",
                      lg: node?.thumbnail645w ?? "",
                      xl: node?.thumbnail645w ?? "",
                    }}
                  />
                </div>
                <div className="flex min-w-0 flex-1 flex-col px-3">
                  <Text maxLines={1}>{node?.title}</Text>
                  <Text variant="body2" maxLines={1} className="text-fg-2">
                    {node?.creatorRolesString}
                  </Text>
                </div>
              </div>
              <div className="flex-shrink-0">
                <ChevronRight size="20" />
              </div>
            </Link>
          )
        })}
      </div>
    </MetadataSectionContainer>
  )
}
